import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import "../AboutUs/AboutUs.css";
import "../../styles/profileSearchHighlight.css";
import ProfileEditModeOverview, {
  getProfileOverviewPageTitle,
  type ProfileOverviewBrand,
} from "./ProfileEditModeOverview";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import { getPersonByIdOrLegacyId } from "../../Services/Person";
import { getOfficeByFirm } from "../../Services/Office";
import { IPerson } from "../../interfaces/models/IPerson";
import { IOfficeNew } from "../../interfaces/models/IOffice";
import AuthContext from "../../store/AuthContext";
import { PersonEmpty } from "../../redux/emptyStates";
import type { PersonSocialNetwork } from "../../interfaces/models/IPersonCreate";
import Eye2 from "../../components/Atomos/Icons/Eye";
import PencilEdit from "../../components/Atomos/Icons/PencilEdit";
import { SKIP_PERSON_LOADER_SEARCH_PARAM } from "../../components/Organismos/Person/AddProfessional";
import { useDomTextHighlight } from "../../hooks/useDomTextHighlight";
import { PROFILE_HIGHLIGHT_QUERY_PARAM } from "../../utils/domTextHighlight";

const VIEW_AS_PUBLIC_QUERY = "viewAsPublic";

function breadcrumbLabelsForPathPrefixes(pathname: string, label: string): Record<string, string> {
  const segments = pathname.split("/").filter(Boolean);
  const labels: Record<string, string> = {};
  let acc = "";
  for (const seg of segments) {
    acc += `/${seg}`;
    labels[acc.toLowerCase()] = label;
  }
  return labels;
}

type ProfileMemberOverviewPageProps = {
  profileTheme?: ProfileOverviewBrand;
};

const ProfileMemberOverviewPage: React.FC<ProfileMemberOverviewPageProps> = ({
  profileTheme = "member",
}) => {
  const { customPath, generatedId } = useParams<{
    customPath: string;
    generatedId: string;
  }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const highlightTerm = searchParams.get(PROFILE_HIGHLIGHT_QUERY_PARAM);
  const profileHighlightRootRef = useRef<HTMLDivElement>(null);
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const { loggedPerson } = useContext(AuthContext);

  const [person, setPerson] = useState<IPerson | null>(null);
  const [offices, setOffices] = useState<IOfficeNew[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!generatedId) {
      setLoading(false);
      setError("Missing profile id");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const p = await getPersonByIdOrLegacyId(generatedId);
        if (cancelled) {
          return;
        }
        if (!p) {
          setPerson(null);
          setError("Profile not found.");
          return;
        }
        setPerson(p);
        if (p.firm) {
          const list = await getOfficeByFirm(p.firm);
          if (!cancelled) {
            setOffices(list ?? []);
          }
        } else {
          setOffices([]);
        }
      } catch {
        if (!cancelled) {
          setError("Could not load profile.");
          setPerson(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [generatedId]);

  useDomTextHighlight(profileHighlightRootRef, highlightTerm, {
    enabled: !loading && Boolean(person),
    contentVersion: person?._ts,
  });

  const viewAsPublic =
    searchParams.get(VIEW_AS_PUBLIC_QUERY) === "1" ||
    searchParams.get(VIEW_AS_PUBLIC_QUERY) === "true";

  useEffect(() => {
    if (!person) {
      return;
    }
    const isOwn =
      !!loggedPerson?.userId &&
      !!person.userId &&
      loggedPerson.userId === person.userId;
    const title = getProfileOverviewPageTitle(isOwn && !viewAsPublic);

    /* Staff app: always show “Edit Profile” in the title bar (Manage Firm professional editor). */
    if (profileTheme === "staff") {
      const firmId = person.firm?.trim();
      const editStaffHref =
        firmId && generatedId
          ? `/ManageFirm/Professionals/${firmId}/Edit/${generatedId}?${SKIP_PERSON_LOADER_SEARCH_PARAM}=1`
          : undefined;
      setPageMeta({
        title,
        breadcrumbLabels: breadcrumbLabelsForPathPrefixes(location.pathname, title),
        titleAction: editStaffHref
          ? {
              label: "Edit Profile",
              to: editStaffHref,
              tone: "secondary",
              icon: (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-white">
                  <PencilEdit className="h-full w-full" />
                </span>
              ),
            }
          : null,
        titleActionSecondary: null,
      });
      return () => clearPageMeta();
    }

    const editMyProfileHref =
      isOwn && customPath && generatedId
        ? `/profile2/${customPath}/${generatedId}/edit`
        : undefined;
    const viewPublicProfileHref =
      isOwn && customPath && generatedId
        ? `${location.pathname}?${VIEW_AS_PUBLIC_QUERY}=1`
        : undefined;

    if (isOwn && viewAsPublic) {
      setPageMeta({
        title,
        breadcrumbLabels: breadcrumbLabelsForPathPrefixes(location.pathname, title),
        titleAction: {
          label: "Go back to My Profile",
          to: location.pathname,
          icon: (
            <Eye2 className="flex h-5 w-5 shrink-0 items-center justify-center text-white" />
          ),
        },
        titleActionSecondary: null,
      });
    } else {
      setPageMeta({
        title,
        breadcrumbLabels: breadcrumbLabelsForPathPrefixes(location.pathname, title),
        titleAction: editMyProfileHref
          ? {
              label: "Edit My Profile",
              to: editMyProfileHref,
              legacyButtonColor: "accent",
              legacyButtonStyle: "filled",
            }
          : null,
        titleActionSecondary:
          isOwn && viewPublicProfileHref
            ? {
                label: "View Public Profile",
                to: viewPublicProfileHref,
                legacyButtonColor: "primary",
                legacyButtonStyle: "filled",
                icon: (
                  <Eye2 className="flex h-5 w-5 shrink-0 items-center justify-center" />
                ),
              }
            : null,
      });
    }
    return () => clearPageMeta();
  }, [
    person,
    location.pathname,
    location.search,
    viewAsPublic,
    loggedPerson?.userId,
    customPath,
    generatedId,
    profileTheme,
    setPageMeta,
    clearPageMeta,
  ]);

  const isOwnProfile = useMemo(
    () =>
      !!loggedPerson?.userId &&
      !!person?.userId &&
      loggedPerson.userId === person.userId,
    [loggedPerson?.userId, person?.userId]
  );

  /** True only when this is your profile and we are not in “view as public” preview mode. */
  const showOwnProfileEditChrome = isOwnProfile && !viewAsPublic;

  const handleProfilePhotoUpdated = (photoTimestamp: string) => {
    setPerson((prev) => (prev ? { ...prev, photo: photoTimestamp } : prev));
  };

  const handleSocialMediaSaved = (socialNetworks: PersonSocialNetwork[]) => {
    setPerson((prev) => (prev ? { ...prev, socialNetworks } : prev));
  };

  const reloadPerson = useCallback(async () => {
    if (!generatedId) {
      return;
    }
    try {
      const p = await getPersonByIdOrLegacyId(generatedId, { cacheBust: true });
      if (p) {
        setPerson(p);
      }
    } catch {
      /* keep current person on failure */
    }
  }, [generatedId]);

  return (
    <div ref={profileHighlightRootRef}>
      <ProfileEditModeOverview
        person={person ?? PersonEmpty}
        offices={offices ?? []}
        isOwnProfile={showOwnProfileEditChrome}
        onProfilePhotoUpdated={handleProfilePhotoUpdated}
        onSocialMediaSaved={handleSocialMediaSaved}
        onAreasOfPracticeUpdated={reloadPerson}
        onGeneralProfileUpdated={reloadPerson}
        onProfessionalCareerUpdated={reloadPerson}
        profileTheme={profileTheme}
      />
    </div>
  );
};

export default ProfileMemberOverviewPage;

/** Staff app: same member profile data and behavior, blue section/title accents. */
export function ProfileStaffOverviewPage() {
  return <ProfileMemberOverviewPage profileTheme="staff" />;
}
